import { z } from 'zod';

/**
 * Persisted workflow node types. `WorkflowNodeType` in the SDK is typed against
 * this list, so adding a node type without a data schema fails to compile.
 */
export const WORKFLOW_NODE_TYPES = [
  'agent',
  'for-each',
  'ad-hoc-agent',
  'complete-tool-call',
  'tool-call',
  'filter',
  'debounce',
  'run-workflow',
  'emit-event',
  'mapper',
  'bucket',
  'group-by',
] as const;

export type WorkflowNodeTypeValue = (typeof WORKFLOW_NODE_TYPES)[number];

/** Stable: these strings are persisted in filter node `data`. */
export const WORKFLOW_FILTER_OPERATORS = [
  'equals',
  'not-equals',
  'contains',
  'not-contains',
  'exists',
  'not-exists',
  'greater-than',
  'less-than',
  'greater-than-or-equal',
  'less-than-or-equal',
  'one-of',
  'not-one-of',
] as const;

export const WORKFLOW_FILTER_COMBINATORS = ['and', 'or'] as const;

/**
 * Window sizes a debounce node truncates to, and which day a week-window opens
 * on. Persisted in node data, so they live with the schemas for the same reason
 * the filter operators do.
 */
export const WORKFLOW_DEBOUNCE_WINDOWS = [
  'hour',
  'day',
  'week',
  'month',
] as const;
export const WORKFLOW_DEBOUNCE_WEEK_STARTS = ['monday', 'sunday'] as const;

/**
 * Bucket flush combinator and reducer ops. Kept in lockstep with the SDK's
 * `bucket-flush-policy.ts` (which owns the runtime interpretation); duplicated
 * here rather than imported because `@cotera/api` must not depend on the SDK.
 */
export const BUCKET_COMBINATORS = ['or', 'and'] as const;
export const BUCKET_REDUCER_OPS = [
  'sum',
  'avg',
  'min',
  'max',
  'first',
  'last',
  'count',
  'distinct',
] as const;

const filterOperatorSchema = z.enum(WORKFLOW_FILTER_OPERATORS);
const filterCombinatorSchema = z.enum(WORKFLOW_FILTER_COMBINATORS);
const runModeSchema = z.enum(['auto', 'manual']);

// ---------------------------------------------------------------------------
// Persisted (read) schemas
//
// These parse `data` exactly as it sits in `workflow_nodes` and
// `workflow_run_definition_nodes`. Two rules keep old rows loading:
//  - Objects are loose. Every SDK builder spreads caller `data` into the node,
//    so rows carry keys no schema names; stripping them would lose data on the
//    next save.
//  - A field is only required when the runtime already refuses a node without
//    it. Optional fields that were read defensively fall back to `undefined`
//    instead of failing the whole node.
// ---------------------------------------------------------------------------

/** Display text: absent, non-string, and blank all read as `undefined`. */
const displayText = z
  .string()
  .refine((value) => value.trim() !== '')
  .optional()
  .catch(undefined);

const lenientString = z.string().optional().catch(undefined);
const lenientNullableString = z.string().nullable().optional().catch(undefined);

/** Ref bindings keep their string entries; anything else is dropped. */
const bindings = z
  .record(z.string(), z.unknown())
  .transform(
    (record): Record<string, string> =>
      Object.fromEntries(
        Object.entries(record).filter(
          (entry): entry is [string, string] => typeof entry[1] === 'string'
        )
      )
  )
  .optional()
  .catch(undefined);

const interruptableToolSchema = z
  .object({
    toolKey: z.string(),
    name: z.string(),
    description: z.string(),
    inputSchema: z.record(z.string(), z.unknown()),
  })
  .optional()
  .catch(undefined);

const toolCallFields = z.looseObject({
  key: z.string(),
  // Optional here so a missing key reports through `missingParams` below.
  params: z.unknown().optional(),
  name: displayText,
  /** Tool param key -> JSON Pointer into immediate upstream step input. */
  paramBindings: bindings,
  /**
   * Inline definition for ad-hoc `client/*` interruptable tools. Required when
   * `key` is under the client/ namespace and the tool is not registered
   * elsewhere.
   */
  interruptableTool: interruptableToolSchema,
});

// `params: undefined` is valid; a node that never set the key is not.
const hasParams = (data: Record<string, unknown>): boolean => 'params' in data;
const missingParams = {
  message: 'tool-call node is missing params',
  path: ['params'],
};

export const workflowNodeDataSchemas = {
  agent: z.looseObject({
    agentId: z.string(),
    name: displayText,
    /** Resume an existing conversation instead of starting a new one. */
    conversationId: lenientString,
  }),
  'for-each': z.looseObject({
    /** Literal list typed by the user; iterated verbatim, wins over itemsPath. */
    items: z.array(z.unknown()).optional(),
    itemsPath: z.string().optional(),
    sourceRunNodeId: z.string().optional(),
    /** Loop-body successor among outgoing edges (fan-out vs post-barrier). */
    childNodeId: z.string().optional(),
    /** Display-only: body node type for canvas badges (body node is hidden). */
    bodyType: displayText,
    /** Display-only: body node name for canvas badges. */
    bodyName: displayText,
  }),
  'ad-hoc-agent': z.looseObject({
    model: z.string(),
    toolKeys: z.array(z.string()).optional(),
    system: lenientString,
    mode: runModeSchema.optional().catch(undefined),
    agent: lenientNullableString,
    agentId: lenientNullableString,
    outputShape: lenientString,
    conversationId: lenientString,
    parentConversationId: lenientNullableString,
    parentToolCallId: lenientNullableString,
  }),
  'complete-tool-call': z.looseObject({
    parentConversationId: z.string(),
    toolCallId: z.string(),
    parentModel: z.string(),
    mode: runModeSchema.optional().catch(undefined),
  }),
  'tool-call': toolCallFields.refine(hasParams, missingParams),
  // Deliberately untyped: `parseFilterConfig` in the SDK interprets filter data,
  // including its three legacy shapes, scalar values and the user-facing
  // messages for unknown operators. Typing fields here would mask those.
  filter: z.looseObject({}),
  debounce: z.looseObject({
    /**
     * Field identifying what is being debounced: a JSON Pointer into the
     * upstream output, or a `NodeName/pointer` upstream reference.
     */
    keyPath: lenientString,
    window: z.enum(WORKFLOW_DEBOUNCE_WINDOWS).optional().catch(undefined),
    /**
     * IANA zone the window boundary falls in. Not validated here: the SDK's
     * `parseDebounceConfig` checks it against the runtime's own zone table,
     * which is the only list that can be right.
     */
    timezone: lenientString,
    weekStartsOn: z
      .enum(WORKFLOW_DEBOUNCE_WEEK_STARTS)
      .optional()
      .catch(undefined),
  }),
  'run-workflow': z.looseObject({
    targetWorkflowId: z.string(),
    /** Pre-generated id for the target workflow run. */
    targetRunId: lenientString,
    name: displayText,
    /** Entry field name -> upstream output ref. */
    inputBindings: bindings,
  }),
  'emit-event': z.looseObject({
    /** Free-form org-scoped event name listeners match on. */
    eventName: z.string().trim().min(1, 'eventName must not be blank'),
    /** Payload field name -> upstream output ref. */
    payloadBindings: bindings,
  }),
  mapper: z.looseObject({
    name: displayText,
    /** Output field name -> upstream ref, or a string with `{{ref}}` tokens. */
    fieldBindings: bindings,
    /**
     * `fieldBindings` keys in authored order. JSONB drops object key order, and
     * a `row` mapper's columns depend on it.
     */
    fieldOrder: z.array(z.string()).optional().catch(undefined),
    /** `record` (default): one object. `row`: `{ [rowKey]: string[] }`. */
    output: z.enum(['record', 'row']).optional().catch(undefined),
    /** The property a `row` mapper publishes its list under; `row` if unset. */
    rowKey: displayText,
  }),
  // Deliberately untyped: `parseBucketConfig` in the SDK interprets bucket data
  // (the flush policy, its count/time triggers, and the reducers), producing the
  // user-facing messages for a malformed policy. Typing fields here would mask
  // those, exactly as with `filter`.
  bucket: z.looseObject({}),
  // Deliberately untyped, for the same reason as `filter` and `bucket`:
  // `parseGroupByConfig` in the SDK interprets group-by data and produces the
  // user-facing message for a missing or malformed pointer. Typing the fields
  // here would mask those.
  'group-by': z.looseObject({}),
} satisfies { [K in WorkflowNodeTypeValue]: z.ZodType };

export const AgentNodeDataSchema = workflowNodeDataSchemas.agent;
export const ForEachNodeDataSchema = workflowNodeDataSchemas['for-each'];
export const AdHocAgentNodeDataSchema = workflowNodeDataSchemas['ad-hoc-agent'];
export const CompleteToolCallNodeDataSchema =
  workflowNodeDataSchemas['complete-tool-call'];
export const ToolCallNodeDataSchema = workflowNodeDataSchemas['tool-call'];
export const FilterNodeDataSchema = workflowNodeDataSchemas.filter;
export const DebounceNodeDataSchema = workflowNodeDataSchemas.debounce;
export const RunWorkflowNodeDataSchema =
  workflowNodeDataSchemas['run-workflow'];
export const EmitEventNodeDataSchema = workflowNodeDataSchemas['emit-event'];
export const MapperNodeDataSchema = workflowNodeDataSchemas.mapper;
export const BucketNodeDataSchema = workflowNodeDataSchemas.bucket;
export const GroupByNodeDataSchema = workflowNodeDataSchemas['group-by'];

export type ParsedNodeData<T> =
  | { readonly t: 'valid'; readonly data: T }
  | { readonly t: 'invalid'; readonly issues: readonly string[] };

/** Parses node data with a specific schema; never throws. */
export function parseNodeDataWithSchema<S extends z.ZodType>(
  schema: S,
  data: unknown
): ParsedNodeData<z.output<S>> {
  const result = schema.safeParse(data);
  if (!result.success) {
    return {
      t: 'invalid',
      issues: result.error.issues.map((issue) => {
        const path = issue.path.map(String).join('.');
        return path === '' ? issue.message : `${path}: ${issue.message}`;
      }),
    };
  }
  return { t: 'valid', data: result.data };
}

export type WorkflowNodeDataByType = {
  [K in WorkflowNodeTypeValue]: z.output<(typeof workflowNodeDataSchemas)[K]>;
};

export type ParsedWorkflowNodeData<K extends WorkflowNodeTypeValue> =
  | { readonly t: 'valid'; readonly data: WorkflowNodeDataByType[K] }
  | { readonly t: 'invalid'; readonly issues: readonly string[] };

function toParsed<K extends WorkflowNodeTypeValue>(
  result: z.ZodSafeParseResult<unknown>
): ParsedWorkflowNodeData<K> {
  if (!result.success) {
    return {
      t: 'invalid',
      issues: result.error.issues.map((issue) => {
        const path = issue.path.map(String).join('.');
        return path === '' ? issue.message : `${path}: ${issue.message}`;
      }),
    };
  }
  return { t: 'valid', data: result.data as WorkflowNodeDataByType[K] };
}

export function parseWorkflowNodeData<K extends WorkflowNodeTypeValue>(
  type: K,
  data: unknown
): ParsedWorkflowNodeData<K> {
  return toParsed(workflowNodeDataSchemas[type].safeParse(data));
}

// ---------------------------------------------------------------------------
// Save schemas
//
// What a node must satisfy before it is written: the read schema plus the ids
// the runtime can't do anything useful without. Reads stay lenient so old rows
// keep loading; saves are where a blank id should be caught.
// ---------------------------------------------------------------------------

const requiredId = (field: string) =>
  z
    .string()
    .refine((value) => value.trim() !== '', `${field} must not be blank`);

export const workflowNodeSaveSchemas = {
  ...workflowNodeDataSchemas,
  agent: workflowNodeDataSchemas.agent.extend({
    agentId: requiredId('agentId'),
  }),
  'tool-call': toolCallFields
    .extend({ key: requiredId('key') })
    .refine(hasParams, missingParams),
  'run-workflow': workflowNodeDataSchemas['run-workflow'].extend({
    targetWorkflowId: requiredId('targetWorkflowId'),
  }),
} satisfies { [K in WorkflowNodeTypeValue]: z.ZodType };

/** Validates node data for a write. Callers still persist the data they were given. */
export function parseWorkflowNodeDataForSave<K extends WorkflowNodeTypeValue>(
  type: K,
  data: unknown
): ParsedWorkflowNodeData<K> {
  return toParsed(workflowNodeSaveSchemas[type].safeParse(data));
}

// ---------------------------------------------------------------------------
// Input (write) schemas for create/update requests
// ---------------------------------------------------------------------------

/** One field/operator/value predicate within a filter node. */
const filterConditionSchema = z.object({
  // JSON Pointer into the upstream output; '' targets the whole value.
  path: z.string().optional(),
  operator: filterOperatorSchema.optional(),
  value: z.string().optional(),
});

/** A group of conditions combined by its `combinator` (defaults to AND). */
const filterGroupSchema = z.object({
  combinator: filterCombinatorSchema.optional(),
  conditions: z.array(filterConditionSchema).optional(),
});

/** One flush trigger: a pending-count threshold, or a cron/time schedule. */
const bucketTriggerSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('count'), threshold: z.number() }),
  z.object({
    kind: z.literal('time'),
    cron: z.string(),
    timezone: z.string(),
  }),
]);

/** One named aggregation over the accumulated items, surfaced under `reduced`. */
const bucketReducerSchema = z.object({
  op: z.enum(BUCKET_REDUCER_OPS),
  path: z.string(),
  as: z.string(),
});

/** Typed node payload for create/update, discriminated on node `type`. */
export const workflowNodeInputSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('agent'),
    data: z.object({ agentId: z.string(), name: z.string().optional() }),
  }),
  z.object({
    type: z.literal('for-each'),
    data: z.object({
      items: z.array(z.unknown()).optional(),
      itemsPath: z.string().optional(),
      sourceRunNodeId: z.string().optional(),
      childNodeId: z.string().optional(),
      bodyType: z.string().optional(),
      bodyName: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('tool-call'),
    data: z.object({
      key: z.string(),
      params: z.unknown().optional(),
      paramBindings: z.record(z.string(), z.string()).optional(),
      name: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('filter'),
    // Current shape is `{ combinator, groups: [{ combinator, conditions }] }`.
    // `conditions` (a single AND group) and the top-level path/operator/value
    // (legacy single condition) are still accepted so older nodes round-trip.
    data: z.object({
      combinator: filterCombinatorSchema.optional(),
      groups: z.array(filterGroupSchema).optional(),
      conditions: z.array(filterConditionSchema).optional(),
      path: z.string().optional(),
      operator: filterOperatorSchema.optional(),
      value: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('debounce'),
    data: z.object({
      /**
       * Field identifying what is being debounced: a JSON Pointer into the
       * upstream output, or a `NodeName/pointer` upstream reference.
       */
      keyPath: z.string(),
      window: z.enum(WORKFLOW_DEBOUNCE_WINDOWS).optional(),
      /**
       * IANA zone the window boundary falls in. Validated by the SDK's
       * `parseDebounceConfig` against the runtime's own zone table, which is
       * the only list that can be right.
       */
      timezone: z.string().optional(),
      weekStartsOn: z.enum(WORKFLOW_DEBOUNCE_WEEK_STARTS).optional(),
    }),
  }),
  z.object({
    type: z.literal('emit-event'),
    data: z.object({
      eventName: z.string(),
      payloadBindings: z.record(z.string(), z.string()).optional(),
    }),
  }),
  z.object({
    type: z.literal('mapper'),
    data: z.object({
      name: z.string().optional(),
      fieldBindings: z.record(z.string(), z.string()).optional(),
      fieldOrder: z.array(z.string()).optional(),
      output: z.enum(['record', 'row']).optional(),
      rowKey: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('run-workflow'),
    data: z.object({
      targetWorkflowId: z.string(),
      name: z.string().optional(),
      inputBindings: z.record(z.string(), z.string()).optional(),
    }),
  }),
  z.object({
    type: z.literal('bucket'),
    // The SDK's `parseBucketConfig` still owns runtime interpretation, but the
    // create/update payload is typed here so the graph mapper can build a
    // `BucketNode` without re-parsing. The persisted read schema stays loose.
    data: z.object({
      name: z.string().optional(),
      policy: z.object({
        combinator: z.enum(BUCKET_COMBINATORS).optional(),
        triggers: z.array(bucketTriggerSchema),
      }),
      accumulate: z.string().optional(),
      reducers: z.array(bucketReducerSchema).optional(),
    }),
  }),
  z.object({
    type: z.literal('group-by'),
    // Typed here for the same reason as `bucket`: the graph mapper builds the
    // node from this payload without re-parsing, while the SDK's
    // `parseGroupByConfig` still owns runtime interpretation and the persisted
    // read schema stays loose.
    data: z.object({
      name: z.string().optional(),
      itemsPath: z.string().optional(),
      keyPath: z.string(),
      /** Stable id of the upstream node the list pointer reads (default: direct predecessor). */
      sourceRunNodeId: z.string().optional(),
    }),
  }),
]);

export type WorkflowNodeInput = z.infer<typeof workflowNodeInputSchema>;
